// The ballot paper — one item, as one member sees it.
//
// Every rule this file follows is here to prevent a specific real-world
// misreport, so each is commented where it lands rather than in a doc nobody
// opens next to the code:
//
//  • Native <fieldset>/<legend>/<input type="radio">, with the <label> wrapping
//    the whole row. Arrow-key traversal, group announcement and "2 of 3"
//    position come free from the platform; a div-and-role re-implementation
//    gives none of them and fails jsx-a11y, which is blocking in this repo.
//  • The yardstick — what the motion NEEDS — is on the voting paper, not beside
//    the result afterwards, and ABOVE the radios rather than under them (see the
//    comment at its site: below them it is off-screen behind the pinned action
//    bar on a phone, at the exact scroll position where the choice is marked).
//  • Open/closed is a filled vs hollow dot AND the word. Never colour alone.
//  • Abstain is a listed option at equal weight; `blank` and not-voting are two
//    further, distinct states (see MarkableChoice in ./paper).
//  • NOT here, deliberately: any tally, turnout, countdown or progress bar. A
//    visible tally during an open secret ballot changes votes.
//  • THE NOTICE AFTER SUBMIT NEVER RESTATES THE MARK. It used to — this file
//    built the sentence "You marked “Aye”." and rendered it inside a
//    `role="status"` live region, so the choice was both printed back on screen
//    and spoken aloud in the room. CAST-PATH.md:213-220 forbids both by name.
//    The notice is now a `<CastStatus>` whose only prop is a closed union of ACT
//    tokens, so this file no longer HAS a string to put in it. See that file's
//    header for what a reviewer should check, and why no lint rule can.

import { useId, useState, type FormEvent, type JSX } from 'react';
import { CastStatus, type CastAct } from './CastStatus';
import { HOUSE_LABEL, VOTE_OPTIONS, type BallotPaperView, type MarkableChoice } from './paper';
import { Yardstick } from './Yardstick';

interface BallotPaperProps {
  view: BallotPaperView;
}

export function BallotPaper({ view }: BallotPaperProps): JSX.Element {
  const { paper, elector } = view;
  const [choice, setChoice] = useState<MarkableChoice | null>(null);
  const [act, setAct] = useState<CastAct>('idle');
  // One radio group per rendered paper; useId keeps the name unique if a second
  // item is ever rendered on the same screen.
  const groupName = useId();
  const isOpen = view.state === 'open';

  // There is no submission from THIS paper — the motion cast path is unbuilt, and
  // this screen renders an invented fixture. The button is real and says so out
  // loud rather than pretending: a control that silently does nothing is the one
  // thing worse than a control that isn't there.
  //
  // ONE TOKEN, AND IT DOES NOT DEPEND ON THE MARK. The previous version chose
  // between two sentences on whether anything was marked, and the marked one
  // quoted the label back. Both are gone, and not only the quotation: whether a
  // paper is blank is itself a fact about its content, so the notice does not
  // branch on it at all. `preview` is the same word whatever is on the paper.
  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAct('preview');
  };

  return (
    <form className="ballot" onSubmit={onSubmit}>
      <p className="ballot-eyebrow">
        <span>
          Item {view.itemNumber} of {view.itemCount}
        </span>
        <span className="ballot-state">
          {/* Shape, not hue: filled = open, hollow = closed — and the word is
              right beside it, so the dot is never the only carrier. */}
          <span
            className={isOpen ? 'ballot-dot' : 'ballot-dot ballot-dot-hollow'}
            aria-hidden="true"
          />
          <span className="ballot-sr">Voting </span>
          {isOpen ? 'Open' : 'Closed'}
        </span>
      </p>

      <h1 className="ballot-title">{paper.item.title}</h1>

      {/* Who you are voting AS, always visible. Your House decides which tally
          your vote lands in, so it belongs on the paper, not in a menu. */}
      <p className="ballot-elector-lead">You are voting as</p>
      <p className="ballot-elector">
        {elector.name} · {HOUSE_LABEL[elector.house]}
      </p>

      <hr className="ballot-rule" />

      {/* The instrument, verbatim and entire. */}
      <div className="ballot-instrument">
        <p className="ballot-instrument-text">{paper.instrument}</p>
        <dl className="ballot-attrib">
          <div className="ballot-attrib-row">
            <dt>Moved</dt>
            <dd>{paper.mover}</dd>
          </div>
          <div className="ballot-attrib-row">
            <dt>Second</dt>
            <dd>{paper.seconder}</dd>
          </div>
        </dl>
      </div>

      <hr className="ballot-rule" />

      {/* ABOVE the fieldset, not below it. On a desktop the order is harmless —
          the submit is inline under the yardstick, so you cannot reach it
          without passing it. On a phone the action bar is PINNED from the
          moment the radios come into view, so a yardstick placed after them is
          skippable and is off-screen at exactly the scroll position where a
          member marks a choice. Miranda's ruling, 2026-08-15, from the render:
          "the order is only safe on the viewport where nobody needs the help".
          thresholds.ts says the same thing in its opening line — what the
          motion needs is said on the paper BEFORE the vote is cast.

          The section itself lives in ./Yardstick, because the results sheet
          shows the same sentence beside the tally and a second copy of it is how
          the two screens start disagreeing. `level={2}` because the motion title
          above is this page's h1. */}
      <Yardstick base={paper.item.base} level={2} />

      <fieldset className="ballot-votes">
        <legend className="ballot-legend">Your vote</legend>
        {VOTE_OPTIONS.map((option) => (
          <label className="vote-row" key={option.value}>
            <input
              className="radio vote-radio"
              type="radio"
              name={groupName}
              value={option.value}
              checked={choice === option.value}
              disabled={!isOpen}
              onChange={() => setChoice(option.value)}
            />
            <span className="vote-label">{option.label}</span>
          </label>
        ))}
        <p className="ballot-note">
          Abstaining is a recorded choice. It is not the same as not voting.
        </p>
      </fieldset>

      {/* ABOVE the action bar, not below it, and for the same phone-shaped
          reason the yardstick is above the radios: `.action-bar` is PINNED over
          the bottom of the scroll port at 640px and under, so a notice placed
          after it in the flow sits behind it at the moment it is written. It is
          the paper's only live region — and it cannot carry the mark; see
          ./CastStatus. */}
      <CastStatus act={act} />

      {/* .action-bar is the shared vocabulary (MOB-2/MOB-5): a plain row on a
          desktop, pinned to the bottom of the viewport inside the thumb zone on
          a phone, with the safe-area inset already handled. */}
      <div className="action-bar ballot-actions">
        <button
          type="submit"
          className="btn btn-primary btn-block ballot-submit"
          disabled={!isOpen}
        >
          Submit my vote
        </button>
      </div>
    </form>
  );
}
